// BoardBand (round 2, a board composition): the top of the day board. On the left the date as the
// heading, and one muted line under it: when it's as of, then the day in words ("14 visits, 3 working,
// 4 done, 1 running late, 2 no one yet"), red only for what needs someone. On the right, in one row of
// round 1's 34 px controls: ‹ Today ›, the rows switch, Book a visit. While the company's records load
// it says so and nothing can be booked (BL-158 5); the words never name where the records come from.
// After the figures, a quiet link to the work waiting to be scheduled, while there is some (until the
// board's job table has its own tab for it).
// While the board's divisions are being arranged (concept A, 26 Oct) the band says the mode instead: its
// heading ("Set up divisions"), one line of what to do, and its own controls (Start empty, Cancel, Done),
// under a strong rule. One band and one primary: the date and Book a visit go while it lasts, as a
// division is for every day, not this one. The first time (Set up divisions), a state word over its
// heading says it isn't set up yet: ink with a dashed underline, the system's mark for waiting (DESIGN.md
// §3a; Setup's .chip.warn), never red, which is only for a problem. Reads only --band-*.
import { Fragment, type ReactNode } from 'react';
import { Button } from '../Button/Button.tsx';
import { Link } from '../Link/Link.tsx';
import { Segmented } from '../Segmented/Segmented.tsx';
import type { Density } from '../time-span.tsx';
import './BoardBand.css';

export interface BandFigure {
  /** "14 visits", "1 running late". */
  words: string;
  /** Needs someone: drawn red. */
  bad?: boolean;
}

export interface BandEditing {
  /** "Set up divisions", "Arranging divisions". */
  title: string;
  /** What to do, in one line. */
  line: string;
  /** Its controls: Start empty, Cancel, Done (the primary, last). */
  controls: ReactNode;
  /** A state word over the heading, for what isn't set up yet ("Not set up yet"). */
  state?: string;
}

export interface BoardBandProps {
  /** "Sunday 25 October". */
  date: string;
  /** "10:30 a.m." on today; nothing on another day. */
  asOf?: string | null;
  figures: BandFigure[];
  isToday?: boolean;
  density?: Density;
  readOnly?: boolean;
  onDay?: (step: 'prev' | 'today' | 'next') => void;
  onDensity?: (density: Density) => void;
  onBook?: () => void;
  /** Book a visit shows: off for someone who can't book (a bookkeeper). */
  canBook?: boolean;
  /** The rows switch shows: off on a touch screen, which keeps the roomy rows' targets (BL-100). */
  showDensity?: boolean;
  /** The work waiting to be scheduled: "12 to schedule ›", drawn only above zero. */
  toSchedule?: { count: number; href: string; onPress?: () => void };
  /** The board is in a mode (arranging divisions): the band says it, with the mode's controls. */
  editing?: BandEditing | null;
}

export function BoardBand({
  date,
  asOf,
  figures,
  isToday = true,
  density = 'comfortable',
  readOnly = false,
  onDay,
  onDensity,
  onBook,
  canBook = true,
  showDensity = true,
  toSchedule,
  editing,
}: BoardBandProps) {
  if (editing)
    return (
      <div className="fl-band" data-editing="" role="region" aria-label={editing.title}>
        <div className="fl-band__title">
          {editing.state ? <p className="fl-band__state">{editing.state}</p> : null}
          {/* focus lands here when the mode opens, so its line is read first and Tab reaches its controls */}
          <h2 className="fl-band__date" tabIndex={-1} data-band-mode="">
            {editing.title}
          </h2>
          <p className="fl-band__line">{editing.line}</p>
        </div>
        <div className="fl-band__controls">{editing.controls}</div>
      </div>
    );
  const day = figures.length ? figures : [{ words: 'Nothing booked' }];
  return (
    <div className="fl-band">
      <div className="fl-band__title">
        <h2 className="fl-band__date">{date}</h2>
        <p className="fl-band__line">
          {readOnly ? (
            <span className="fl-band__wait">Read only while the company’s records load. </span>
          ) : null}
          {asOf ? `As of ${asOf}: ` : null}
          {day.map((f, i) => (
            <Fragment key={f.words}>
              {i ? ' ' : ''}
              <span className="fl-band__figure" data-bad={f.bad ? '' : undefined}>
                {f.words}
                {i < day.length - 1 ? ',' : ''}
              </span>
            </Fragment>
          ))}
          {toSchedule && toSchedule.count > 0 ? (
            <>
              {' '}
              {/* one piece, never broken across lines (QA-765) */}
              <span className="fl-band__figure">
                <Link href={toSchedule.href} {...(toSchedule.onPress ? { onPress: toSchedule.onPress } : {})}>
                  {toSchedule.count} to schedule ›
                </Link>
              </span>
            </>
          ) : null}
        </p>
      </div>
      <div className="fl-band__controls">
        <Segmented
          label="Day"
          selectionMode="none"
          items={[
            { id: 'prev', label: 'Previous day', icon: 'chevron-left' },
            { id: 'today', label: 'Today' },
            { id: 'next', label: 'Next day', icon: 'chevron-right' },
          ]}
          on={isToday ? 'today' : undefined}
          onAction={(id) => onDay?.(id as 'prev' | 'today' | 'next')}
        />
        {showDensity ? (
          <Segmented
            label="Rows"
            items={[
              { id: 'comfortable', label: 'Roomy rows', icon: 'rows-comfortable' },
              { id: 'compact', label: 'Compact rows', icon: 'rows-compact' },
            ]}
            selectedKey={density}
            onSelectionChange={(id) => onDensity?.(id as Density)}
          />
        ) : null}
        {canBook ? (
          <Button variant="primary" icon="plus" isDisabled={readOnly} onPress={onBook}>
            Book a visit
          </Button>
        ) : null}
      </div>
    </div>
  );
}
